/**
 * Add a car by hand to today's Daily Photo List (Ian, 2026-10-11). The car
 * lands at the top of the sheet picked here and lives on today's list only —
 * the next list is built from the vendor files. Design and rulings:
 * .claude/plans/photo-list-added-cars.md.
 *
 * The VIN and the stock are required (circles and notes key on the VIN; the
 * previous-stay rule and automatic Done work from the stock — Ian,
 * 2026-10-12). Year/make/model fill from the VIN lookup the moment the VIN
 * reaches 17 characters and stay editable; color and lot are optional.
 *
 * Modal chrome and the lookup flow copy components/photos/AddVehicleModal.tsx
 * (decode effect ~line 80, styles ~line 299) — there is no shared Dialog
 * component yet in INVI.
 */
import React, { useEffect, useRef, useState } from "react"

import { decodeVin } from "../../api/photoCapture"
import type { AddedVehicleRequest } from "../../api/photoList"
import { THEME } from "../../config/theme"
import { PHOTO_LIST_SHEETS, type PhotoListSheetSlug } from "../../context/PhotoListContext"
import { useIsMobile } from "../../hooks/useIsMobile"
import { tint } from "../../utils/color"

const VIN_LENGTH = 17
// No I, O or Q — they never appear in a VIN (same rule as the server and the
// app's VINValidator).
const VIN_CHARACTERS = /^[A-HJ-NPR-Z0-9]+$/
// Mirror AddedVehicleRequest's limits in backend/schemas.py, so the server
// never answers with a validation error the person can't read.
const MIN_YEAR = 1900
const MAX_YEAR = 2100
const YEAR_PATTERN = /^\d{4}$/
const STOCK_MAX_LENGTH = 32
const TEXT_MAX_LENGTH = 64

/** The values the last VIN lookup typed in, so the next lookup can clear
 * exactly those (and never something the person typed themselves). */
interface LookupFill {
  year: string
  make: string
  model: string
}

const EMPTY_FILL: LookupFill = { year: "", make: "", model: "" }

/** A cleared field stays cleared only if it still holds what the previous
 * lookup put there; anything else is the person's own typing. */
function clearIfFromLookup(current: string, filled: string): string {
  return filled !== "" && current === filled ? "" : current
}

interface Props {
  defaultSheet: PhotoListSheetSlug
  onClose: () => void
  /** Saves the car; rejects with the sentence to show (e.g. "That car is
   * already on Toyota USED."). The modal closes on success. */
  onSubmit: (request: AddedVehicleRequest) => Promise<unknown>
}

export default function AddCarToListModal({ defaultSheet, onClose, onSubmit }: Props) {
  const isMobile = useIsMobile()
  const [vin, setVin] = useState("")
  const [sheet, setSheet] = useState<PhotoListSheetSlug>(defaultSheet)
  const [year, setYear] = useState("")
  const [make, setMake] = useState("")
  const [model, setModel] = useState("")
  const [color, setColor] = useState("")
  const [stock, setStock] = useState("")
  const [lot, setLot] = useState("")

  const [decoding, setDecoding] = useState(false)
  const [decodeNotice, setDecodeNotice] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Re-decode only when the 17-character VIN changes to a NEW one, so
  // editing back and forth doesn't call NHTSA on every keystroke.
  const lastDecodedVinRef = useRef<string | null>(null)
  const lastFillRef = useRef<LookupFill>(EMPTY_FILL)

  const normalizedVin = vin.trim().toUpperCase()
  const vinIsComplete =
    normalizedVin.length === VIN_LENGTH && VIN_CHARACTERS.test(normalizedVin)
  const canSubmit = vinIsComplete && stock.trim() !== "" && !submitting

  // Fill year/make/model from the VIN lookup once the VIN is complete.
  useEffect(() => {
    if (!vinIsComplete) {
      lastDecodedVinRef.current = null
      // A lookup cancelled by the edit never reaches its finally.
      setDecoding(false)
      return
    }
    if (lastDecodedVinRef.current === normalizedVin) return
    let cancelled = false
    lastDecodedVinRef.current = normalizedVin
    // A new VIN is a new car: drop what the previous lookup filled in, so
    // a fixed typo can't save the old car's year/make/model (review,
    // 2026-10-12). Values the person typed themselves stay.
    const previousFill = lastFillRef.current
    lastFillRef.current = EMPTY_FILL
    setYear((current) => clearIfFromLookup(current, previousFill.year))
    setMake((current) => clearIfFromLookup(current, previousFill.make))
    setModel((current) => clearIfFromLookup(current, previousFill.model))
    setDecoding(true)
    setDecodeNotice(null)
    ;(async () => {
      try {
        const result = await decodeVin(normalizedVin)
        if (cancelled) return
        const fill: LookupFill = {
          year: result.year !== null ? String(result.year) : "",
          make: result.make ?? "",
          model: result.model ?? "",
        }
        lastFillRef.current = fill
        if (fill.year) setYear(fill.year)
        if (fill.make) setMake(fill.make)
        if (fill.model) setModel(fill.model)
        if (result.year === null && !result.make && !result.model) {
          setDecodeNotice("We couldn't fill in this VIN. Type the year, make and model below.")
        }
      } catch {
        if (!cancelled) {
          setDecodeNotice("The VIN lookup didn't answer. Type the year, make and model below.")
        }
      } finally {
        if (!cancelled) setDecoding(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [normalizedVin, vinIsComplete])

  // Escape closes, like the notes modal.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [onClose])

  const handleSubmit = async () => {
    setSubmitError(null)
    if (!vinIsComplete) {
      setSubmitError("A VIN is 17 letters and numbers, without I, O or Q.")
      return
    }
    if (stock.trim() === "") {
      setSubmitError("Enter the car's stock number.")
      return
    }
    const trimmedYear = year.trim()
    const parsedYear = trimmedYear === "" ? null : Number(trimmedYear)
    if (
      parsedYear !== null &&
      (!YEAR_PATTERN.test(trimmedYear) || parsedYear < MIN_YEAR || parsedYear > MAX_YEAR)
    ) {
      setSubmitError("Year must be 4 digits, like 2024.")
      return
    }
    setSubmitting(true)
    try {
      await onSubmit({
        vin: normalizedVin,
        sheet,
        year: parsedYear,
        make: make.trim() || null,
        model: model.trim() || null,
        color_ext: color.trim() || null,
        stock: stock.trim(),
        stargard: lot.trim() || null,
      })
      onClose()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Could not add the car.")
      setSubmitting(false)
    }
  }

  const fieldRow: React.CSSProperties = {
    ...rowFieldsStyle,
    ...(isMobile && { flexDirection: "column", gap: 0 }),
  }

  return (
    <div style={backdropStyle} onClick={onClose}>
      <div
        role="dialog"
        aria-label="Add a car to today's list"
        style={{
          ...dialogStyle,
          ...(isMobile && {
            minWidth: 0,
            width: "calc(100vw - 24px)",
            padding: THEME.spacing.md,
          }),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={headerStyle}>
          <h2 style={titleStyle}>Add a car</h2>
          <button type="button" onClick={onClose} style={closeButtonStyle} aria-label="Close">
            ×
          </button>
        </div>

        <p style={hintStyle}>
          The car goes to the top of its sheet on today&apos;s list, tagged ADDED.
          The next list is built from the vendor files and won&apos;t include it.
        </p>

        <label style={fieldStyle}>
          <span style={fieldLabelStyle}>
            VIN {decoding && <span style={inlineStatusStyle}> — looking up…</span>}
          </span>
          <input
            type="text"
            value={vin}
            // Spaces dropped as typed or pasted: a VIN has none, and a
            // length cap here cut a pasted " VIN" short (review, 2026-10-12).
            onChange={(e) => setVin(e.target.value.replace(/\s+/g, ""))}
            placeholder="17-character VIN"
            // Shown in capitals as typed; the saved VIN is uppercased anyway.
            style={{ ...inputStyle, fontFamily: THEME.fonts.mono, textTransform: "uppercase" }}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
          />
          {normalizedVin.length > 0 && (
            <span style={fieldHintStyle}>
              {normalizedVin.length}/{VIN_LENGTH} characters
            </span>
          )}
        </label>

        {/* Stock sits right under the VIN (Ian, 2026-10-11) and is required
            (Ian, 2026-10-12). */}
        <label style={fieldStyle}>
          <span style={fieldLabelStyle}>Stock #</span>
          <input
            type="text"
            value={stock}
            onChange={(e) => setStock(e.target.value)}
            style={inputStyle}
            maxLength={STOCK_MAX_LENGTH}
          />
        </label>

        {decodeNotice && <div style={noticeStyle}>{decodeNotice}</div>}

        <label style={fieldStyle}>
          <span style={fieldLabelStyle}>Sheet</span>
          <select
            value={sheet}
            onChange={(e) => setSheet(e.target.value as PhotoListSheetSlug)}
            style={inputStyle}
          >
            {PHOTO_LIST_SHEETS.map((option) => (
              <option key={option.slug} value={option.slug}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <div style={fieldRow}>
          <label style={{ ...fieldStyle, ...(isMobile ? {} : { flex: "0 0 90px" }) }}>
            <span style={fieldLabelStyle}>Year</span>
            <input
              type="text"
              inputMode="numeric"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              style={inputStyle}
              maxLength={4}
            />
          </label>
          <label style={{ ...fieldStyle, flex: 1 }}>
            <span style={fieldLabelStyle}>Make</span>
            <input type="text" value={make} onChange={(e) => setMake(e.target.value)} style={inputStyle} maxLength={TEXT_MAX_LENGTH} />
          </label>
          <label style={{ ...fieldStyle, flex: 1 }}>
            <span style={fieldLabelStyle}>Model</span>
            <input type="text" value={model} onChange={(e) => setModel(e.target.value)} style={inputStyle} maxLength={TEXT_MAX_LENGTH} />
          </label>
        </div>

        <div style={fieldRow}>
          <label style={{ ...fieldStyle, flex: 1 }}>
            <span style={fieldLabelStyle}>Color (optional)</span>
            <input type="text" value={color} onChange={(e) => setColor(e.target.value)} style={inputStyle} maxLength={TEXT_MAX_LENGTH} />
          </label>
          <label style={{ ...fieldStyle, flex: 1 }}>
            <span style={fieldLabelStyle}>Lot (optional)</span>
            <input type="text" value={lot} onChange={(e) => setLot(e.target.value)} style={inputStyle} maxLength={TEXT_MAX_LENGTH} />
          </label>
        </div>

        {submitError && <div style={errorStyle}>{submitError}</div>}

        <div style={footerStyle}>
          <button type="button" onClick={onClose} style={secondaryButtonStyle}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={primaryButtonStyle(canSubmit)}
          >
            {submitting ? "Adding…" : "Add to list"}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Styles — copied from components/photos/AddVehicleModal.tsx (~line 299),
// except the backdrop and shadow, which use THEME.modal (review, 2026-10-12).
// ---------------------------------------------------------------------------

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: THEME.modal.overlay,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
}

const dialogStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  borderRadius: THEME.borderRadius.lg,
  padding: THEME.spacing.lg,
  minWidth: 520,
  maxWidth: 620,
  maxHeight: "90vh",
  overflowY: "auto",
  fontFamily: THEME.fonts.primary,
  boxShadow: THEME.modal.shadow,
}

const headerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: THEME.spacing.sm,
}

const titleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
  margin: 0,
}

const closeButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  fontSize: 24,
  cursor: "pointer",
  color: THEME.colors.neutral.light.textSecondary,
  lineHeight: 1,
}

const hintStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  margin: 0,
  marginBottom: THEME.spacing.md,
}

const fieldStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.xs,
  marginBottom: THEME.spacing.md,
}

const rowFieldsStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.sm,
}

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const inlineStatusStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 400,
  textTransform: "none",
  letterSpacing: 0,
  color: THEME.colors.neutral.light.textSecondary,
}

const fieldHintStyle: React.CSSProperties = {
  fontSize: 11,
  color: THEME.colors.neutral.light.textSecondary,
}

const inputStyle: React.CSSProperties = {
  fontSize: 13,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: THEME.fonts.primary,
}

const noticeStyle: React.CSSProperties = {
  fontSize: 12,
  background: tint(THEME.auditTable.tabColors.pending, 0.5),
  border: `1px solid ${THEME.auditTable.tabColors.pending}`,
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  color: THEME.colors.neutral.light.textPrimary,
  marginBottom: THEME.spacing.md,
}

const errorStyle: React.CSSProperties = {
  fontSize: 12,
  background: tint(THEME.colors.semantic.danger, 0.1),
  border: `1px solid ${THEME.colors.semantic.danger}`,
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  color: THEME.colors.semantic.danger,
  marginBottom: THEME.spacing.md,
}

const footerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: THEME.spacing.sm,
}

const primaryButtonStyle = (enabled: boolean): React.CSSProperties => ({
  fontSize: 13,
  fontWeight: 500,
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  borderRadius: THEME.borderRadius.md,
  border: "none",
  background: enabled ? THEME.colors.navy : THEME.colors.silver,
  color: THEME.colors.white,
  cursor: enabled ? "pointer" : "not-allowed",
  fontFamily: THEME.fonts.primary,
})

const secondaryButtonStyle: React.CSSProperties = {
  fontSize: 13,
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  borderRadius: THEME.borderRadius.md,
  border: `1px solid ${THEME.colors.silver}`,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}
